// AICA AI Feedback Analytics Page
// Displays review votes, accuracy metrics, and correction tracking
import { createFileRoute } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { DashboardBody } from '@/components/layout/DashboardBody';
import { PeriodBar, type Period } from '@/components/layout/PeriodBar';
import { KpiCardV2 } from '@/components/dashboard';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { CheckCircle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { cn } from '@/lib/utils';
import { FeatureGate } from '@/components/ui/FeatureGate';
import { ComingSoonOverlay } from '@/components/dashboard/coming-soon-overlay';

// ============================================================================
// TYPES
// ============================================================================

type Vote = 'up' | 'down';

interface ReviewItem {
  id: string;
  vote: Vote;
  recordId: string;
  agentKey: string;
  aiScore: number;
  reviewerKey: string;
  commentKey: string;
  date: string;
  relativeTimeKey: string;
  flagged: boolean;
}

type SubTabId = 'all' | 'accuracy' | 'corrected';

// ============================================================================
// MOCK DATA — TODO: Replace with API call when endpoint available
// ============================================================================

const MOCK_REVIEWS: ReviewItem[] = [
  {
    id: 'rv_001',
    vote: 'up',
    recordId: 'rec_a1b2c3d4',
    agentKey: 'mariaK',
    aiScore: 92,
    reviewerKey: 'elenaS',
    commentKey: 'accurateSentiment',
    date: '2026-02-23',
    relativeTimeKey: '2hAgo',
    flagged: false,
  },
  {
    id: 'rv_002',
    vote: 'down',
    recordId: 'rec_e5f6g7h8',
    agentKey: 'alexeiP',
    aiScore: 54,
    reviewerKey: 'dmitryV',
    commentKey: 'missedCompliance',
    date: '2026-02-23',
    relativeTimeKey: '3hAgo',
    flagged: true,
  },
  {
    id: 'rv_003',
    vote: 'up',
    recordId: 'rec_i9j0k1l2',
    agentKey: 'olgaN',
    aiScore: 87,
    reviewerKey: 'mariaK',
    commentKey: 'minorDiarization',
    date: '2026-02-23',
    relativeTimeKey: '4hAgo',
    flagged: false,
  },
  {
    id: 'rv_004',
    vote: 'down',
    recordId: 'rec_m3n4o5p6',
    agentKey: 'ivanS',
    aiScore: 41,
    reviewerKey: 'elenaS',
    commentKey: 'wrongLanguage',
    date: '2026-02-22',
    relativeTimeKey: '1dAgo',
    flagged: true,
  },
  {
    id: 'rv_005',
    vote: 'up',
    recordId: 'rec_q7r8s9t0',
    agentKey: 'dariaK',
    aiScore: 95,
    reviewerKey: 'alexeiP',
    commentKey: 'perfectAnalysis',
    date: '2026-02-22',
    relativeTimeKey: '1dAgo',
    flagged: false,
  },
  {
    id: 'rv_006',
    vote: 'up',
    recordId: 'rec_u1v2w3x4',
    agentKey: 'mariaK',
    aiScore: 78,
    reviewerKey: 'dmitryV',
    commentKey: 'acceptableQuality',
    date: '2026-02-22',
    relativeTimeKey: '1dAgo',
    flagged: false,
  },
  {
    id: 'rv_007',
    vote: 'down',
    recordId: 'rec_y5z6a7b8',
    agentKey: 'alexeiP',
    aiScore: 63,
    reviewerKey: 'olgaN',
    commentKey: 'scoreInflation',
    date: '2026-02-21',
    relativeTimeKey: '2dAgo',
    flagged: true,
  },
  {
    id: 'rv_008',
    vote: 'up',
    recordId: 'rec_c9d0e1f2',
    agentKey: 'ivanS',
    aiScore: 88,
    reviewerKey: 'mariaK',
    commentKey: 'consistentManual',
    date: '2026-02-21',
    relativeTimeKey: '2dAgo',
    flagged: false,
  },
];

// ============================================================================
// HELPERS
// ============================================================================

function scoreColor(score: number): string {
  if (score >= 80) return 'text-success';
  if (score >= 60) return 'text-primary';
  if (score >= 40) return 'text-warning';
  return 'text-danger';
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function ReviewsTable({ reviews }: { reviews: ReviewItem[] }) {
  const { t } = useTranslation('analyses');
  return (
    <DashboardPanel
      title={t('aiFeedback.table.title', 'Reviews')}
      action={
        <span className="text-xs text-fg-muted">
          {t('aiFeedback.table.total', {
            count: reviews.length,
            defaultValue: '{{count}} total',
          })}
        </span>
      }
    >
      <div className="overflow-x-auto -m-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-medium text-fg-muted">
                {t('aiFeedback.table.headers.vote', 'Vote')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-fg-muted">
                {t('aiFeedback.table.headers.record', 'Record')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-fg-muted">
                {t('aiFeedback.table.headers.agent', 'Agent')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-fg-muted">
                {t('aiFeedback.table.headers.aiScore', 'AI Score')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-fg-muted">
                {t('aiFeedback.table.headers.reviewer', 'Reviewer')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-fg-muted">
                {t('aiFeedback.table.headers.comment', 'Comment')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-fg-muted">
                {t('aiFeedback.table.headers.date', 'Date')}
              </th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => (
              <tr
                key={review.id}
                className={cn(
                  'border-b border-border hover:bg-bg-elevated transition-colors',
                  review.flagged && 'bg-danger-dim'
                )}
              >
                {/* Vote */}
                <td className="px-4 py-3 text-sm text-center">
                  {review.vote === 'up' ? (
                    <ThumbsUp
                      className="h-4 w-4 inline text-success"
                      aria-label="Thumbs up"
                    />
                  ) : (
                    <ThumbsDown
                      className="h-4 w-4 inline text-danger"
                      aria-label="Thumbs down"
                    />
                  )}
                </td>
                {/* Record ID */}
                <td className="px-4 py-3">
                  <span className="font-mono text-primary hover:underline cursor-pointer">
                    {review.recordId.slice(0, 12)}...
                  </span>
                </td>
                {/* Agent */}
                <td className="px-4 py-3 text-fg">
                  {t(`aiFeedback.names.${review.agentKey}`, review.agentKey)}
                </td>
                {/* AI Score */}
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      'font-mono font-semibold tabular-nums',
                      scoreColor(review.aiScore)
                    )}
                  >
                    {review.aiScore}
                  </span>
                </td>
                {/* Reviewer */}
                <td className="px-4 py-3 text-fg">
                  {t(`aiFeedback.names.${review.reviewerKey}`, review.reviewerKey)}
                </td>
                {/* Comment */}
                <td className="px-4 py-3">
                  <span className="text-xs text-fg-secondary max-w-[200px] truncate block">
                    {t(`aiFeedback.comments.${review.commentKey}`, review.commentKey)}
                  </span>
                </td>
                {/* Date */}
                <td className="px-4 py-3 text-xs text-fg-faint whitespace-nowrap">
                  {t(
                    `aiFeedback.relativeTime.${review.relativeTimeKey}`,
                    review.relativeTimeKey
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardPanel>
  );
}

// ============================================================================
// PAGE COMPONENT
// ============================================================================

function AiFeedbackPage() {
  const { t } = useTranslation('analyses');
  useDocumentTitle(t('aiFeedback.metaTitle', 'AI Feedback'));

  const [activePeriod, setActivePeriod] = useState<Period>('week');
  const [activeSubTab, setActiveSubTab] = useState<SubTabId>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // TODO: Replace with API call when endpoint available
  const [reviews] = useState<ReviewItem[]>(MOCK_REVIEWS);

  const correctedReviews = reviews.filter((r) => r.vote === 'down');

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    // TODO: Replace with actual data refetch
    setTimeout(() => setIsRefreshing(false), 800);
  }, []);

  const subTabs = [
    {
      id: 'all',
      label: t('aiFeedback.subTabs.all', {
        count: reviews.length,
        defaultValue: 'All Reviews ({{count}})',
      }),
    },
    { id: 'accuracy', label: t('aiFeedback.subTabs.accuracy', 'Accuracy Analytics') },
    {
      id: 'corrected',
      label: t('aiFeedback.subTabs.corrected', {
        count: correctedReviews.length,
        defaultValue: 'Corrected ({{count}})',
      }),
    },
  ];

  return (
    <DashboardBody
      title={t('aiFeedback.pageTitle', 'AI Feedback')}
      subtitle={t(
        'aiFeedback.pageSubtitle',
        'Review votes, accuracy metrics, and corrections'
      )}
      icon={ThumbsUp}
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
          <BreadcrumbItem current>
            {t('aiFeedback.breadcrumb', 'AI Feedback')}
          </BreadcrumbItem>
        </Breadcrumb>
      }
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
    >
      {/* Period Bar + Sub-tabs */}
      <PeriodBar
        periods={[
          { id: 'today', label: t('aiFeedback.period.today', 'Today') },
          { id: 'week', label: t('aiFeedback.period.week', 'Week') },
          { id: 'month', label: t('aiFeedback.period.month', 'Month') },
        ]}
        activePeriod={activePeriod}
        onPeriodChange={setActivePeriod}
        subTabs={subTabs}
        activeSubTab={activeSubTab}
        onSubTabChange={(tab) => setActiveSubTab(tab as SubTabId)}
      />

      <ComingSoonOverlay>
        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCardV2
            label={t('aiFeedback.kpi.totalReviews', 'Total Reviews')}
            value={reviews.length}
            color="primary"
          />
          <KpiCardV2
            label={t('aiFeedback.kpi.accuracyRate', 'Accuracy Rate')}
            value="91.4%"
            color="success"
            trend={{ value: 2.1, direction: 'up' }}
          />
          <KpiCardV2
            label={t('aiFeedback.kpi.needsCorrection', 'Needs Correction')}
            value={correctedReviews.length}
            color="warning"
          />
          <KpiCardV2
            label={t('aiFeedback.kpi.avgCorrectionTime', 'Avg Correction Time')}
            value={t('aiFeedback.kpi.avgCorrectionTimeValue', '2.3 min')}
            color="info"
          />
        </div>

        {/* Tab content */}
        {activeSubTab === 'all' && <ReviewsTable reviews={reviews} />}

        {activeSubTab === 'accuracy' && (
          <DashboardPanel title={t('aiFeedback.accuracy.title', 'Accuracy Analytics')}>
            <div className="flex flex-col items-center justify-center py-12 text-fg-muted">
              <CheckCircle className="h-8 w-8 mb-3 text-fg-subtle" />
              <p className="text-sm">
                {t('aiFeedback.accuracy.comingSoon', 'Accuracy analytics coming soon')}
              </p>
              <p className="text-xs mt-1">
                {t(
                  'aiFeedback.accuracy.description',
                  'Detailed accuracy breakdowns by agent, criteria, and time period'
                )}
              </p>
            </div>
          </DashboardPanel>
        )}

        {activeSubTab === 'corrected' && <ReviewsTable reviews={correctedReviews} />}
      </ComingSoonOverlay>
    </DashboardBody>
  );
}

// ============================================================================
// ROUTE
// ============================================================================

export const Route = createFileRoute('/analytics/ai-feedback')({
  component: function AiFeedbackPageGated() {
    return (
      <FeatureGate flag="enableAIFeedback">
        <AiFeedbackPage />
      </FeatureGate>
    );
  },
});
